import {
  doc,
  getDoc,
  collection,
  addDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  writeBatch,
  arrayUnion,
  setDoc
} from 'firebase/firestore';
// import { ref, listAll, getBytes, deleteObject, uploadBytes, getDownloadURL } from 'firebase/storage'; // Firebase Storage
import { db, auth } from './config'; // Removed storage import
import { updateUserReputation, ACTIVITY_TYPES } from './userReputation'; // Import reputation system
import { FEATURE_LABELS } from '../constants/siteFeatures';
// import { getAllFeatures } from './features'; // This might not be needed if features are handled locally or differently
import { listR2Objects, moveR2Object, getR2PublicUrl } from '../services/r2Service'; // R2 Service

// Helper function to sanitize features with source information
function sanitizeFeatures(features) {
  const sanitized = {};
  if (!features) return sanitized;
  
  Object.keys(features).forEach(key => {
    const feature = features[key];
    if (!feature || typeof feature !== 'object') return;

    sanitized[key] = {
      enabled: Boolean(feature.enabled),
      autoDetected: Boolean(feature.autoDetected),
      confidence: feature.confidence || null,
      source: feature.source || (feature.autoDetected ? 'auto' : 'manual')
    };
  });
  return sanitized;
}

// Helper function to move files from temp R2 prefix to permanent R2 prefix
async function moveTempFiles(siteId, mediaItemsToProcess) {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      console.warn('moveTempFiles: No user ID, cannot determine temp prefix.');
      return mediaItemsToProcess.map(item => ({ ...item, error: 'User ID missing for temp file move.' }));
    }

    const tempPrefix = `temp-uploads/${userId}/`; // R2 prefix for temporary files
    const permanentSitePrefix = `sites/${siteId}/media/`; // R2 prefix for permanent site media

    // List objects in the user's temporary R2 prefix
    // Note: listR2Objects returns an object like { objects: [ { key: '...' }, ... ] }
    const tempR2ObjectsResult = await listR2Objects(tempPrefix);
    const tempR2FileKeys = tempR2ObjectsResult.objects ? tempR2ObjectsResult.objects.map(obj => obj.key) : [];

    if (tempR2FileKeys.length === 0) {
      console.log(`No temp files found in R2 at prefix: ${tempPrefix}`);
      // If no temp files, but mediaItemsToProcess has items, they might be direct uploads or already processed.
      // Mark them as not found in temp, or handle as per logic.
      return mediaItemsToProcess.map(item => {
        if (item.path && item.path.startsWith(tempPrefix)) { // If it was expected to be in temp
          return { ...item, error: `Expected temp file ${item.path} not found in R2.` };
        }
        return item; // Otherwise, pass through
      });
    }
    
    const processedMediaItems = await Promise.all(
      mediaItemsToProcess.map(async (item) => {
        // item.path should be the R2 objectKey from the upload step (e.g., "temp-uploads/USER_ID/filename.jpg")
        const originalObjectKey = item.path;

        // Check if this item's key exists in the listed temp files
        if (!originalObjectKey || !tempR2FileKeys.includes(originalObjectKey)) {
          console.warn(`Temp file for item ${item.name} (key: ${originalObjectKey}) not found in R2 temp prefix ${tempPrefix}. Skipping move for this item.`);
          return { ...item, error: `Temp file ${originalObjectKey} not found for move.` };
        }

        try {
          const fileName = originalObjectKey.substring(tempPrefix.length); // Extract filename
          const destinationKey = `${permanentSitePrefix}${fileName}`;

          // Move the object in R2
          await moveR2Object(originalObjectKey, destinationKey);
          
          // Update media item with new R2 objectKey and public URL
          return {
            ...item,
            url: getR2PublicUrl(destinationKey), // Generate new public URL
            path: destinationKey, // Update path to the new R2 objectKey
            error: null // Clear any previous error
          };
        } catch (error) {
          console.error(`Error moving R2 object from ${originalObjectKey} to permanent storage:`, error);
          return { ...item, error: `Failed to move R2 object: ${error.message}` };
        }
      })
    );

    return processedMediaItems;
  } catch (error) {
    console.error('Error in moveTempFiles (R2):', error);
    // Return original media items, marked with an error
    return mediaItemsToProcess.map(item => ({ ...item, error: `Major error during temp file processing: ${error.message}` }));
  }
}

export const getSiteProfile = async (siteId) => {
  try {
    const siteRef = doc(db, 'sites', siteId);
    const siteSnap = await getDoc(siteRef);

    if (!siteSnap.exists()) {
      return { success: false, error: 'Site not found' };
    }

    const siteData = siteSnap.data();

    // Get all available features from site.features
    const features = siteData.features || {};

    // Clean up any invalid feature data
    Object.keys(features).forEach(key => {
      if (!features[key] || typeof features[key] !== 'object') {
        delete features[key];
      } else {
        features[key] = {
          enabled: Boolean(features[key].enabled),
          autoDetected: Boolean(features[key].autoDetected),
          confidence: features[key].confidence || null,
          label: features[key].label || FEATURE_LABELS[key] || key,
          source: features[key].source || (features[key].autoDetected ? 'auto' : 'manual')
        };
      }
    });

    // Clean up media predictions
    if (Array.isArray(siteData.media)) {
      siteData.media = siteData.media.map(media => ({
        ...media,
        predictions: media.predictions?.map(pred => ({
          class: pred.class,
          confidence: pred.confidence,
          x: pred.x,
          y: pred.y,
          width: pred.width,
          height: pred.height,
          source: pred.source || (pred.manual ? 'manual' : 'auto')
        })).filter(Boolean) || []
      }));
    }

    return {
      success: true,
      data: {
        id: siteSnap.id,
        ...siteData,
        features
      }
    };
  } catch (error) {
    console.error('Error fetching site profile:', error);
    return { success: false, error: 'Failed to fetch site profile' };
  }
};

// Import the sync function, ensure it's properly imported from the correct location
import { syncNewSiteToBeta } from './syncNewSiteToBeta';

export const createSite = async (siteData) => {
  const batch = writeBatch(db); // Initialize batch write
  const sitesCollectionRef = collection(db, 'sites');
  const mediaCollectionRef = collection(db, 'site_media'); // Reference to the site_media collection

  // Define siteId and siteDocRef outside the try block initially
  let siteId = null;
  let siteDocRef = null;

  try {
    // const siteRef = collection(db, 'sites'); // No longer needed directly here
    // Validate required fields
    if (!siteData.name || !siteData.location?.coordinates?.lat || !siteData.location?.coordinates?.lng) {
      return { success: false, error: 'Missing required fields' };
    }

    // Create a document ID upfront for the main site document
    siteDocRef = doc(sitesCollectionRef);
    siteId = siteDocRef.id; // Assign siteId here, BEFORE media processing

    // If there's media, first move it from temporary storage to permanent storage
    let processedMedia = []; // Renamed from updatedMedia for clarity
    let mainImage = siteData.mainImage;
    let tempPrimaryMediaId = siteData.primaryMediaId; // ID from the context before saving
    let finalPrimaryMediaDocId = null; // The actual Firestore doc ID for the primary media
    let primaryMediaDocRef = null; // Ref to the primary media doc in site_media

    if (Array.isArray(siteData.media) && siteData.media.length > 0) {
      try {
        console.log(`Moving temporary files for potential site ID: ${siteId}`);
        // Pass the final siteId to moveTempFiles (function expects 'docId' param name)
        processedMedia = await moveTempFiles(siteId, siteData.media);

        // Update main image URL if needed based on the potentially updated media array
        if (tempPrimaryMediaId) {
          const mainMediaItem = processedMedia.find(item => item.id === tempPrimaryMediaId);
          if (mainMediaItem && mainMediaItem.url) {
            mainImage = mainMediaItem.url;
            // Keep track of which item was intended as primary
            mainMediaItem.intendedAsPrimary = true;
          } else {
             // If the primary media failed to move, try finding another image
             const firstAvailableImage = processedMedia.find(item => item.url && item.type === 'image');
             if (firstAvailableImage) {
               tempPrimaryMediaId = firstAvailableImage.id; // Update which one is intended as primary
               mainImage = firstAvailableImage.url;
               firstAvailableImage.intendedAsPrimary = true;
             } else {
               // No images successfully moved? Clear primary/main image
               tempPrimaryMediaId = null;
               mainImage = null;
             }
          }
        } else if (processedMedia.length > 0) {
           // If no primary was set, but we have moved media, set the first image as primary
           const firstAvailableImage = processedMedia.find(item => item.url && item.type === 'image');
           if (firstAvailableImage) {
             tempPrimaryMediaId = firstAvailableImage.id;
             mainImage = firstAvailableImage.url;
             firstAvailableImage.intendedAsPrimary = true;
           }
        }

      } catch (error) {
        console.error('Error during the file moving process:', error);
        // If moving files fails catastrophically, proceed without media or with original if possible
        processedMedia = siteData.media.map(item => ({ ...item, error: 'Failed to move file' })); // Mark items with error
        mainImage = null; // Clear main image if move failed
        tempPrimaryMediaId = null;
      }
    } else {
      processedMedia = Array.isArray(siteData.media) ? siteData.media : [];
    }

    // Define the newSite object for the batch write
    const newSiteDataForBatch = {
      name: siteData.name || '',
      description: siteData.description || '',
      location: {
        address: siteData.location?.address || '',
        coordinates: {
          lat: Number(siteData.location?.coordinates?.lat) || 0,
          lng: Number(siteData.location?.coordinates?.lng) || 0
        },
        region: siteData.location?.region || '',
        country: siteData.location?.country || ''
      },
      submittedBy: auth.currentUser?.uid || 'anonymous',
      submitterName: auth.currentUser?.displayName || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      verificationStatus: 'pending',
      // Basic site info
      startEra: siteData.startEra || '',
      endEra: siteData.endEra || '',
      culture: siteData.culture || '',
      estimatedEra: siteData.estimatedEra || '',
      materialType: siteData.materialType || '',
      accessibility: siteData.accessibility || '',
      condition: siteData.condition || '',
      // Advanced site info
      culturalSignificance: siteData.culturalSignificance || '',
      constructionPeriods: Array.isArray(siteData.constructionPeriods) ? siteData.constructionPeriods : [],
      // Use the potentially updated main image, but primaryMediaId will be set later in the batch
      mainImage: mainImage || null,
      primaryMediaId: null, // Set later in batch if applicable
      // Set initial main period
      mainPeriodIndex: siteData.mainPeriodIndex ?? 0,
      // Add timeline events
      timelineEvents: Array.isArray(siteData.timelineEvents) ? siteData.timelineEvents : [],
      // DO NOT add media array here
      // Include grounding metadata
      groundingMetadata: siteData.groundingMetadata || null,
      // Add lastMediaUpdate timestamp
      lastMediaUpdate: processedMedia.length > 0 ? serverTimestamp() : null,
      
      // IMPORTANT: Include all the detailed section fields from AI generation
      overviewIntroduction: siteData.overviewIntroduction || '',
      discoveryAndExcavation: siteData.discoveryAndExcavation || '',
      historicalContextAndChronology_narrative: siteData.historicalContextAndChronology_narrative || '',
      references: Array.isArray(siteData.references) ? siteData.references : [], // Use references field
      keyArchitecturalFeaturesLayout: siteData.keyArchitecturalFeaturesLayout || '',
      purposeAndFunction: siteData.purposeAndFunction || '',
      culturalSignificance_narrative: siteData.culturalSignificance_narrative || '',
      notableFindsArtifacts_narrative: siteData.notableFindsArtifacts_narrative || '',
      theoriesAndInterpretations: siteData.theoriesAndInterpretations || '',
      currentStatusAndPreservation_narrative: siteData.currentStatusAndPreservation_narrative || '',
    };

    // Add the main site document creation to the batch
    batch.set(siteDocRef, newSiteDataForBatch); // Use the generated siteDocRef

    // Add media items to the site_media collection via the batch
    if (processedMedia.length > 0) {
      processedMedia.forEach(item => {
        // Skip items that failed to move if they don't have a URL
        if (item.error && !item.url) {
          console.warn(`Skipping media item due to move error and no URL: ${item.name}`);
          return;
        }

        const mediaDocRef = doc(mediaCollectionRef); // Generate ID for the media doc
        const mediaItemData = {
          siteId: siteId, // Link to the main site document using the defined siteId
          url: item.url || '',
          path: item.path || '',
          type: item.type || 'image',
          name: item.name || '',
          title: item.title || item.name || 'Untitled',
          size: item.size || 0,
          uploadedAt: item.uploadedAt || serverTimestamp(),
          uploadedBy: auth.currentUser?.uid || 'anonymous',
          subLocationId: item.subLocationId || null,
          coordinates: item.coordinates || null,
          predictions: item.predictions || [],
          isMain: false // Set to false initially
        };

        // Add media document creation to the batch
        batch.set(mediaDocRef, mediaItemData);

        // Check if this media item was the one intended as primary
        if (item.intendedAsPrimary) {
          finalPrimaryMediaDocId = mediaDocRef.id; // Store the actual Firestore ID
          primaryMediaDocRef = mediaDocRef; // Store the ref to update isMain later
        }
      });

      // If a primary media item was identified, update the main site doc and the media doc in the batch
      if (finalPrimaryMediaDocId && primaryMediaDocRef) {
        batch.update(siteDocRef, { // Use siteDocRef here
          primaryMediaId: finalPrimaryMediaDocId,
          mainImage: mainImage // Ensure mainImage URL is correct
        });
        batch.update(primaryMediaDocRef, { // Use primaryMediaDocRef here
          isMain: true
        });
      } else if (!finalPrimaryMediaDocId && mainImage) {
        // If we have a mainImage URL but couldn't link it to a specific media doc
        // (e.g., only non-image media was uploaded), just save the URL.
         batch.update(siteDocRef, { // Use siteDocRef here
          mainImage: mainImage
        });
      }
    }

    // Commit all writes (main site doc + all media docs + updates)
    await batch.commit();

    // Ensure siteId is valid before returning
    if (!siteId) throw new Error("Cannot return success: siteId is missing after sync.");

    // Sync newly created site to the beta collection for immediate map display
    try {
      console.log('Syncing newly created site to beta collection:', siteId);
      await syncNewSiteToBeta(siteId);
      console.log('New site successfully synced to beta collection:', siteId);
    } catch (syncError) {
      console.error('Failed to sync new site to beta collection:', syncError);
      // Continue without failing the operation, but log the error
    }

    // Construct the final site data to return, excluding the media array
    // Use the data that was actually written in the batch
    const returnedSiteData = { ...newSiteDataForBatch };
    // delete returnedSiteData.media; // Should not be needed as it wasn't added
    returnedSiteData.primaryMediaId = finalPrimaryMediaDocId; // Add the correct final ID

    // Update user reputation for site submission
    if (auth.currentUser) {
      await updateUserReputation(auth.currentUser.uid, ACTIVITY_TYPES.SITE_SUBMISSION);
    }

    return {
      success: true,
      data: {
        id: siteId,
        ...returnedSiteData // Return the cleaned-up site data
      }
    };
  } catch (error) {
    console.error('Error creating site:', error);
    // Consider adding more specific error handling if batch fails etc.
    return { success: false, error: `Failed to create site: ${error.message}` };
  }
};

// New helper function for media-only updates to reduce cascading updates
export const updateSiteMediaOnly = async (siteId, updateData, currentData) => {
  try {
    const siteRef = doc(db, 'sites', siteId);
    const mediaUpdateObject = {
      updatedAt: serverTimestamp(),
      lastMediaUpdate: serverTimestamp() // Track when media was last updated
    };

    // Handle setting a primary media ID
    if ('primaryMediaId' in updateData) {
      mediaUpdateObject.primaryMediaId = updateData.primaryMediaId;
    }

    // Handle setting a main image URL
    if ('mainImage' in updateData) {
      mediaUpdateObject.mainImage = updateData.mainImage;
    }

    // Handle new media items efficiently
    if (updateData.media && Array.isArray(updateData.media)) {
      // Record new media IDs for search index without storing full media objects in site document
      const newMediaIds = updateData.media.map(media => media.id).filter(Boolean);
      if (newMediaIds.length > 0) {
        mediaUpdateObject.recentMediaIds = arrayUnion(...newMediaIds);
      }

      // Process new feature tags without a full document rewrite
      if (!updateData.skipFeatureUpdates) {
        const batch = writeBatch(db);
        
        // Extract features from media predictions
        updateData.media.forEach(media => {
          if (!Array.isArray(media.predictions)) return;
          
          media.predictions.forEach(pred => {
            if (!pred.class) return;
            
            const normalizedClass = pred.class.toLowerCase().replace(/\s+/g, '');
            const fieldPath = `features.${normalizedClass}`;
            
            // Update only this specific feature
            batch.update(siteRef, {
              [fieldPath]: {
                enabled: true,
                autoDetected: pred.source === 'auto',
                confidence: pred.confidence || 1.0,
                source: pred.source || 'auto',
                label: pred.class,
                updatedAt: serverTimestamp()
              }
            });
          });
        });
        
        // Commit all feature updates in a single batch write
        await batch.commit();
      }
    }

    // Update the site document with minimal fields
    await updateDoc(siteRef, mediaUpdateObject);
    
    return { success: true };
  } catch (error) {
    console.error('Error updating site media:', error);
    return { success: false, error: 'Failed to update site media' };
  }
};

export const updateSite = async (siteId, updateData) => {
  try {
    const siteRef = doc(db, 'sites', siteId);
    const siteSnap = await getDoc(siteRef);

    if (!siteSnap.exists()) {
      return { success: false, error: 'Site not found' };
    }

    const currentData = siteSnap.data();
    
    // Determine if this is a media-specific update
    const isMediaUpdate = updateData.media || updateData.mediaUpdate || updateData.primaryMediaId || updateData.mainImage;
    
    // If this is a media-only update, process it efficiently
    if (isMediaUpdate && !updateData.name && !updateData.description) {
      const result = await updateSiteMediaOnly(siteId, updateData, currentData);
      
      // Sync media changes to beta collection
      try {
        await syncNewSiteToBeta(siteId);
        console.log('Site media update synced to beta collection:', siteId);
      } catch (syncError) {
        console.error('Failed to sync media update to beta collection:', syncError);
      }
      
      return result;
    }

    // For non-media updates or mixed updates, process with new efficiency
    // If updating media, process source-based predictions
    if (updateData.media) {
      updateData.media = updateData.media.map(media => ({
        ...media,
        predictions: media.predictions?.map(pred => ({
          class: pred.class,
          confidence: pred.confidence,
          source: pred.source || (pred.manual ? 'manual' : 'auto'),
          x: pred.x,
          y: pred.y,
          width: pred.width,
          height: pred.height,
          // Include training data for manual predictions
          ...(pred.source === 'manual' ? {
            trainingData: pred.trainingData
          } : {})
        })).filter(Boolean) || []
      }));

      // Don't update all features, handle separately through siteProfile.js
      // Incrementally build features for new media only
      if (!updateData.skipFeatureUpdates) {
        const newFeatures = {};
        updateData.media.forEach(media => {
          media.predictions?.forEach(pred => {
            const normalizedClass = pred.class.toLowerCase().replace(/\s+/g, '');
            newFeatures[normalizedClass] = {
              enabled: true,
              autoDetected: pred.source === 'auto',
              confidence: pred.confidence,
              source: pred.source,
              label: pred.class
            };
          });
        });

        // Only include new features, don't overwrite entire features object
        if (Object.keys(newFeatures).length > 0) {
          updateData.newFeatures = newFeatures;
        }
      }
    }

    // Create update object only with fields that exist in updateData
    const updateObject = {
      updatedAt: serverTimestamp()
    };

    // Only include fields that are explicitly set in updateData
    if ('name' in updateData) updateObject.name = updateData.name;
    if ('description' in updateData) updateObject.description = updateData.description;
    if ('location' in updateData) updateObject.location = updateData.location;
    if ('startEra' in updateData) updateObject.startEra = updateData.startEra;
    if ('endEra' in updateData) updateObject.endEra = updateData.endEra;
    if ('culture' in updateData) updateObject.culture = updateData.culture;
    if ('culturalSignificance' in updateData) updateObject.culturalSignificance = updateData.culturalSignificance;
    if ('featured' in updateData) updateObject.featured = updateData.featured;
    if ('constructionPeriods' in updateData) {
      // Ensure all period properties are properly structured
      updateObject.constructionPeriods = updateData.constructionPeriods.map(period => ({
        periodName: period.periodName || '',
        startEra: period.startEra || '',
        endEra: period.endEra || '',
        culture: period.culture || '',
        features: Array.isArray(period.features) ? period.features : []
      }));
    }
    if ('mainImage' in updateData) updateObject.mainImage = updateData.mainImage;
    if ('primaryMediaId' in updateData) updateObject.primaryMediaId = updateData.primaryMediaId;
    if (typeof updateData.mainPeriodIndex === 'number') updateObject.mainPeriodIndex = updateData.mainPeriodIndex;
    if ('timelineEvents' in updateData) updateObject.timelineEvents = Array.isArray(updateData.timelineEvents) ? updateData.timelineEvents : [];
    if ('references' in updateData) updateObject.references = Array.isArray(updateData.references) ? updateData.references : []; // Use references field
    if ('groundingMetadata' in updateData) updateObject.groundingMetadata = updateData.groundingMetadata;
    
    // IMPORTANT: Include all the detailed section fields too
    if ('overviewIntroduction' in updateData) updateObject.overviewIntroduction = updateData.overviewIntroduction;
    if ('discoveryAndExcavation' in updateData) updateObject.discoveryAndExcavation = updateData.discoveryAndExcavation;
    if ('historicalContextAndChronology_narrative' in updateData) updateObject.historicalContextAndChronology_narrative = updateData.historicalContextAndChronology_narrative;
    if ('keyArchitecturalFeaturesLayout' in updateData) updateObject.keyArchitecturalFeaturesLayout = updateData.keyArchitecturalFeaturesLayout;
    if ('purposeAndFunction' in updateData) updateObject.purposeAndFunction = updateData.purposeAndFunction;
    if ('culturalSignificance_narrative' in updateData) updateObject.culturalSignificance_narrative = updateData.culturalSignificance_narrative;
    if ('notableFindsArtifacts_narrative' in updateData) updateObject.notableFindsArtifacts_narrative = updateData.notableFindsArtifacts_narrative;
    if ('theoriesAndInterpretations' in updateData) updateObject.theoriesAndInterpretations = updateData.theoriesAndInterpretations;
    if ('currentStatusAndPreservation_narrative' in updateData) updateObject.currentStatusAndPreservation_narrative = updateData.currentStatusAndPreservation_narrative;
    
    // Handle new features incrementally rather than replacing entire features object
    if (updateData.newFeatures) {
      const batch = writeBatch(db);
      
      Object.entries(updateData.newFeatures).forEach(([featureId, featureData]) => {
        batch.update(siteRef, {
          [`features.${featureId}`]: featureData
        });
      });
      
      // Add the main update to the batch
      batch.update(siteRef, updateObject);
      
      // Execute all updates in a single batch
      await batch.commit();
    } else {
      // Standard update without feature changes
      await updateDoc(siteRef, updateObject);
    }
    
    // Sync site data changes to beta collection
    try {
      await syncNewSiteToBeta(siteId);
      console.log('Site update synced to beta collection:', siteId);
    } catch (syncError) {
      console.error('Failed to sync site update to beta collection:', syncError);
      // Continue without failing the operation
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating site:', error);
    return { success: false, error: 'Failed to update site' };
  }
};
